/**
 * ResizeObserver mixin
 * 用于监听元素尺寸变化
 * @typedef {Object} ResizeConfig
 * @property {string|HTMLElement} target - 目标元素或选择器
 * @property {function(number, number, ResizeObserverEntry): void} callback - 尺寸变化回调函数
 *
 * @example
 * // 在组件中使用
 * import resize_observer_mixin from '@/mixins/resizeObserver';
 *
 * export default {
 *   mixins: [resize_observer_mixin],
 *   mounted() {
 *     // 开始监听元素尺寸变化
 *     const success = this.observeResize([
 *       {
 *         target: '.element1',
 *         callback: (width, height, entry) => console.log('元素1变化:', width, height, entry)
 *       },
 *       {
 *         target: '.element2',
 *         callback: (width, height, entry) => console.log('元素2变化:', width, height, entry)
 *       }
 *     ]);
 *
 *     if (!success) {
 *       console.warn('部分或全部元素监听失败');
 *     }
 *   },
 *   beforeDestroy() {
 *     // 组件销毁时会自动清理，也可以手动调用
 *     this.destroyResizeObserver();
 *   }
 * }
 */

export default {
  data() {
    return {
      /** @type {ResizeObserver|null} ResizeObserver实例 */
      resize_observer: null,
      /** @type {Map<HTMLElement, Function>} 当前观察的元素集合 */
      observed_elements: new Map(),
    };
  },

  beforeDestroy() {
    this.destroyResizeObserver();
  },

  methods: {
    /**
     * 初始化ResizeObserver
     * @private
     * @returns {ResizeObserver|null}
     */
    initResizeObserver() {
      // 如果浏览器不支持，直接返回null
      if (typeof ResizeObserver === 'undefined') {
        console.warn('当前浏览器不支持 ResizeObserver');
        return null;
      }

      // 如果已经初始化，直接返回实例
      if (this.resize_observer) {
        return this.resize_observer;
      }

      // 创建新实例
      this.resize_observer = new ResizeObserver(entries => {
        for (const entry of entries) {
          const callback = this.observed_elements.get(entry.target);
          if (callback) {
            const { width, height } = entry.contentRect;
            callback(width, height, entry);
          }
        }
      });

      return this.resize_observer;
    },

    /**
     * 开始观察元素
     * @private
     * @param {HTMLElement} element - 目标元素
     * @param {Function} callback - 回调函数
     * @returns {boolean} 是否成功开始观察
     */
    startObserving(element, callback) {
      try {
        // 如果元素已经在观察中，先取消之前的观察
        if (this.observed_elements.has(element)) {
          this.resize_observer.unobserve(element);
        }

        // 保存回调并开始观察
        this.observed_elements.set(element, callback);
        this.resize_observer.observe(element);

        // 立即触发一次回调，获取初始尺寸
        callback(element.offsetWidth, element.offsetHeight, {
          target: element,
          contentRect: {
            width: element.offsetWidth,
            height: element.offsetHeight,
          },
        });

        return true;
      } catch (error) {
        console.error('观察元素失败:', error);
        return false;
      }
    },

    /**
     * 获取目标元素
     * @private
     * @param {string|HTMLElement} target - 目标元素或选择器
     * @returns {HTMLElement|null} 目标元素
     */
    getTargetElement(target) {
      try {
        if (target instanceof HTMLElement) {
          return target;
        }
        if (typeof target === 'string') {
          return this.$el.querySelector(target);
        }
        console.warn('无效的目标元素:', target);
        return null;
      } catch (error) {
        console.error('获取目标元素失败:', error);
        return null;
      }
    },

    /**
     * 观察元素尺寸变化
     * @param {ResizeConfig[]} configs - 观察配置数组
     * @returns {boolean} 是否全部成功开始观察
     */
    observeResize(configs) {
      // 参数验证
      if (!Array.isArray(configs)) {
        console.warn('configs 必须是数组');
        return false;
      }

      // 初始化 observer
      const observer = this.initResizeObserver();
      if (!observer) {
        return false;
      }

      // 记录是否全部成功
      let all_success = true;

      // 处理每个配置
      configs.forEach(config => {
        // 验证配置
        if (!config || !config.target || typeof config.callback !== 'function') {
          console.warn('无效的配置:', config);
          all_success = false;
          return;
        }

        // 获取并验证元素
        const element = this.getTargetElement(config.target);
        if (!element) {
          all_success = false;
          return;
        }

        // 开始观察
        if (!this.startObserving(element, config.callback)) {
          all_success = false;
        }
      });

      return all_success;
    },

    /**
     * 取消观察特定元素
     * @param {string|HTMLElement} target - 目标元素或选择器
     * @returns {boolean} 是否成功取消观察
     */
    unobserveResize(target) {
      try {
        if (!this.resize_observer) {
          return false;
        }

        const element = this.getTargetElement(target);
        if (!element || !this.observed_elements.has(element)) {
          return false;
        }

        this.resize_observer.unobserve(element);
        this.observed_elements.delete(element);
        return true;
      } catch (error) {
        console.error('取消观察失败:', error);
        return false;
      }
    },

    /**
     * 销毁观察者实例
     */
    destroyResizeObserver() {
      try {
        if (this.resize_observer) {
          this.resize_observer.disconnect();
          this.resize_observer = null;
          this.observed_elements.clear();
        }
      } catch (error) {
        console.error('销毁观察者失败:', error);
      }
    },
  },
};
